/* ─────────────────────────────────────────────────────────────────────────
   Travel-needs assessment—centralized, data-driven configuration.

   Everything about the interim homepage assessment (questions, order, answer
   options, endpoint labels, and the deterministic result copy) lives here so
   the wording can be revised when Brian provides further guidance WITHOUT
   touching the component that renders it.

   The completed result is kept in local React state only. There is no network
   request and nothing is emailed yet—see the single integration point noted
   in Home.jsx (handleAssessmentComplete).
   ──────────────────────────────────────────────────────────────────────── */

// Session guard: the assessment appears at most once per browser session.
export const ASSESSMENT_SESSION_KEY = "steck_travel_assessment_seen";

// Stable source tag carried on the completion payload.
export const ASSESSMENT_SOURCE = "homepage_travel_needs_assessment";

export const QUESTIONS = [
  {
    id: "workProfile",
    kind: "choice",
    prompt: "Which travel profile best reflects the way you work?",
    options: [
      {
        value: "client_leadership",
        label: "Client & leadership travel",
        body: "Meetings, relationships, and decisions across multiple markets.",
      },
      {
        value: "multi_location",
        label: "Multi-location operations",
        body: "Teams, facilities, projects, or assets spread across different locations.",
      },
      {
        value: "time_sensitive",
        label: "Time-sensitive travel",
        body: "Work where timing, responsiveness, and schedule control matter.",
      },
    ],
  },
  {
    id: "primaryFriction",
    kind: "choice",
    prompt: "Where does commercial travel cost you the most?",
    options: [
      {
        value: "schedule_control",
        label: "Time at home",
        body: "Fixed schedules that stretch a quick trip into a late night, or a night away from home.",
      },
      {
        value: "regional_access",
        label: "Closer airports",
        body: "A long haul to a distant hub when a smaller airport sits much nearer your destination.",
      },
      {
        value: "productive_time",
        label: "Days on the road",
        body: "Whole days lost to connections and waiting for a trip that could be there and back.",
      },
    ],
  },
  {
    id: "impactScore",
    kind: "scale",
    prompt: "How much does that weigh on you?",
    min: 1,
    max: 10,
    minLabel: "Minimal impact",
    maxLabel: "Material constraint",
  },
];

// The three levers Steck is positioned to address. All three are shown on the
// result; the one matching the visitor's primary friction is emphasized.
export const LEVERS = [
  { key: "schedule", line: "Greater schedule control" },
  { key: "access", line: "More direct regional access" },
  { key: "time", line: "Better preservation of productive time" },
];

const FRICTION_LEVER = {
  schedule_control: {
    key: "schedule",
    line: "Greater schedule control",
    detail: "departures set to your calendar, not a fixed timetable",
  },
  regional_access: {
    key: "access",
    line: "More direct regional access",
    detail: "thousands of regional fields closer to where you need to be",
  },
  productive_time: {
    key: "time",
    line: "Better preservation of productive time",
    detail: "fewer connections, less ground time, workdays that stay intact",
  },
};

// Ten interchangeable headlines. All frame the answer as OWNING a private
// aircraft (owner-operated)—never Steck operating a flight, since Steck
// provides acquisition and management services and is not a charter operator.
// One is chosen deterministically so different profiles never read alike.
const HEADLINES = [
  "This is the case for your own aircraft.",
  "This is where ownership earns its keep.",
  "A private aircraft changes this trip.",
  "You're closer than the airlines make it look.",
  "This is what owning an aircraft solves.",
  "This is where the trip gets simpler.",
  "Multiple stops, one day.",
  "Owning the aircraft makes this simple.",
  "This is an ownership kind of trip.",
  "Closer and quicker than it looks.",
];

// A grounded, two-sentence solution for every work-profile x friction pairing.
// COMPLIANCE: the benefit always belongs to OWNING an aircraft (owner-operated,
// on the owner's own terms)—never to Steck operating or selling a flight.
// Steck's role is acquisition and management; it is not a charter operator, so
// no line may frame Steck as flying anyone anywhere. The point in the
// background of all nine: a trip the airlines stretch into several days, with
// the connections, overnights, and hotels that come with it, is often a single
// day in an aircraft of your own. Written plainly, not as marketing.
const SOLUTION = {
  client_leadership: {
    schedule_control:
      "Seeing clients across two or three markets usually means building the week around airline departures. An aircraft of your own puts the schedule in your hands, so that whole round can be a single day—home the same night instead of strung across three.",
    regional_access:
      "Those markets are rarely next to a major hub, so commercial routing adds connections and an overnight or two. Your own aircraft can go direct to the regional fields nearest them, turning several stops that would otherwise take days into one.",
    productive_time:
      "The hours lost to connections and layovers are hours not spent with the people you flew out to see. Owning your own aircraft gives that time back, often compressing a multi-day itinerary into one working day.",
  },
  multi_location: {
    schedule_control:
      "Reaching several of your locations by airline means aligning schedules that rarely line up, which is how a two-stop plan turns into a four-day trip. With an aircraft of your own you set the day, and the whole loop can close before dinner.",
    regional_access:
      "Most people don't realize a run through three or four sites can be a single day in a private aircraft. Commercial connections through distant hubs stretch it across most of a week, with the hotel nights and lost days that come with it.",
    productive_time:
      "A multi-site trip by airline racks up layovers, overnights, and hotels, and the days it burns usually cost more than the seats. An aircraft of your own can cover the whole route in a day and remove most of both.",
  },
  time_sensitive: {
    schedule_control:
      "When timing is fixed, the airlines' next-available departure sets the plan. Owning your own aircraft puts that schedule in your hands—you leave when you need to, and often make it home the same night.",
    regional_access:
      "A same-day, there-and-back run is hard to build on commercial routing but straightforward in a private aircraft of your own. Going direct to the nearest field turns what the airlines spread over two or three days into an afternoon.",
    productive_time:
      "Every delay and connection is time the trip can't spare. An aircraft of your own cuts that out, so work that would eat several days on the airlines is often done and back within one.",
  },
};

const PROFILE_IDX = { client_leadership: 0, multi_location: 1, time_sensitive: 2 };
const FRICTION_IDX = { schedule_control: 0, regional_access: 1, productive_time: 2 };
const rankBucket = (score) => {
  const n = Number(score) || 1;
  return n >= 8 ? 2 : n >= 4 ? 1 : 0;
};

// Related report to lead the visitor to, keyed off the friction (tension). At a
// high tension level they are decision-ready, so route to the economics piece.
const FRICTION_ARTICLE = {
  schedule_control: { slug: "ownership-versus-charter", title: "Aircraft Ownership Versus Charter Utilization" },
  regional_access: { slug: "future-of-regional-business-aviation", title: "The Future of Regional Business Aviation" },
  productive_time: { slug: "mission-profile-analysis", title: "Mission Profile Analysis" },
};
const HIGH_IMPACT_ARTICLE = {
  slug: "economics-of-managed-ownership",
  title: "The Economics of Managed Ownership",
};

/**
 * Deterministic result—the same answers always produce the same copy, but
 * each work-profile × friction × ranking combination gets its own headline, its
 * own tailored solution, and a relevant report to read next. No fabricated
 * savings or unsupported claims; the levers are truthful category strengths.
 */
export function computeResult({ workProfile, primaryFriction, impactScore }) {
  const lever = FRICTION_LEVER[primaryFriction] || FRICTION_LEVER.schedule_control;
  const profile = SOLUTION[workProfile] ? workProfile : "client_leadership";
  const friction = SOLUTION[profile][primaryFriction] ? primaryFriction : "schedule_control";
  const bucket = rankBucket(impactScore);

  // Weighting profile by 3 keeps every profile × friction pairing on a distinct
  // headline at any given ranking; the ranking shifts it again for more variety.
  const hi = (PROFILE_IDX[profile] * 3 + FRICTION_IDX[friction] + bucket) % HEADLINES.length;

  // High tension (8-10) → the economics/decision piece; otherwise the article
  // that matches their friction.
  const recommendedArticle = bucket === 2 ? HIGH_IMPACT_ARTICLE : FRICTION_ARTICLE[friction];

  return {
    resultProfile: `${lever.key}-led`,
    emphasisKey: lever.key,
    headline: HEADLINES[hi],
    body: SOLUTION[profile][friction],
    // Compliance guardrail shown on every result: states Steck's actual role
    // (acquisition + management for owners) so nothing reads as charter/operation.
    positioning:
      "Steck's role is acquisition and management—the aircraft is owned and operated on your own terms.",
    recommendedArticle,
  };
}

/* ═══════════════════════════════════════════════════════════════════════════
   TWO DOORS EXPERIENCE—the unified tool that replaces the old survey.

   The Two Doors overlay reuses the three proven pieces of the old assessment,
   preserved verbatim in meaning:
     • the friction-level question (reworded, executive-forward—SAME values),
     • the tension-ranking 1–10 scale (reworded copy—SAME scoring),
     • the recommended-reading logic (reused as-is: friction × tension bucket).

   The old work-profile question is gone; the recommendation is now driven by
   the NEW inputs—Family vs Business, the selected business industry, the
   friction, and the tension—so no obsolete question influences the result.
   The article/lever/positioning logic below is shared with computeResult so
   there is a single source of truth rather than two competing systems.
   ═══════════════════════════════════════════════════════════════════════════ */

// Preserved friction question—polished, executive-forward wording. The three
// values (schedule_control / regional_access / productive_time) are unchanged,
// so the scoring, lever mapping, and article routing all behave exactly as before.
export const FRICTION_QUESTION = {
  id: "primaryFriction",
  kind: "choice",
  prompt: "Where does travel create the greatest friction in your schedule?",
  options: [
    {
      value: "schedule_control",
      label: "Control of your time",
      body: "Fixed departure banks that turn a short trip into a late night—or a night away you didn't need.",
    },
    {
      value: "regional_access",
      label: "Distance to the airport",
      body: "A long haul to a major hub when a smaller field sits far closer to where you're actually headed.",
    },
    {
      value: "productive_time",
      label: "Days spent in transit",
      body: "Whole days surrendered to connections and layovers for a trip that could be there and back.",
    },
  ],
};

// Preserved tension ranking—same 1–10 scale and scoring, concise wording.
export const TENSION_QUESTION = {
  id: "impactScore",
  kind: "scale",
  prompt: "How much does that weigh on the way you work?",
  min: 1,
  max: 10,
  minLabel: "Manageable",
  maxLabel: "A real constraint",
};

// Seven business worlds. `chip` is the selector label (exact order per brief);
// `eyebrow`/`headline`/`body` are the rotating content card. Copy is verbatim,
// framed around what OWNING the aircraft makes possible (owner-operated)—never
// Steck operating a flight, to stay clear of any charter/operator implication.
export const INDUSTRIES = [
  {
    id: "real_estate",
    chip: "Real Estate / Developer",
    eyebrow: "Real Estate",
    headline: "Be in the room before the other offer is.",
    body: "Property doesn't wait for a convenient flight. Tour the site, sit across from the seller, close on a handshake—in markets the airlines forgot. The deals go to whoever shows up first. Now that's you.",
  },
  {
    id: "construction",
    chip: "Contractor / Builder",
    eyebrow: "Construction",
    headline: "More job sites. Fewer windshield hours.",
    body: "You can't run three sites from a truck seat or a middle seat. Walk the job, meet the crew, win the bid—then be on the next one by afternoon. Bring your foreman and land where the work actually is.",
  },
  {
    id: "energy",
    chip: "Oil & Gas / Energy",
    eyebrow: "Energy",
    headline: "Your assets don't sit next to an airport. Neither should you.",
    body: "When a well's down or a lease is closing, a day lost to connections and rental cars is a day you don't get back. Fly direct from East Texas to the rig, the courthouse, or the deal—handle it, and be home the same night.",
  },
  {
    id: "operations",
    chip: "Multi-Location Owner",
    eyebrow: "Operations",
    headline: "You can't lead a location you never visit.",
    body: "Every store, every manager, every market—on your schedule instead of the airline's. The best operators manage by showing up. Make showing up something you can actually do in a day.",
  },
  {
    id: "land",
    chip: "Rancher / Land & Timber",
    eyebrow: "Land",
    headline: "Your land is remote. Getting to it shouldn't cost you a day.",
    body: "Check the operation, meet the buyer, tour the acreage—even when the nearest terminal is two hours from the gate. Land close to what's yours, on your time, and be back for supper.",
  },
  {
    id: "medical",
    chip: "Healthcare",
    eyebrow: "Medical",
    headline: "Your time is measured in patients, not boarding passes.",
    body: "Cover every facility, every week, without losing a clinical day to layovers. The most valuable hour in medicine is the one you spend where you're needed—not in a terminal.",
  },
  {
    id: "finance",
    chip: "Deal Maker",
    eyebrow: "Finance / Law",
    headline: "Discretion, speed, and a seat in the room.",
    body: "The deal moves at the speed of face-to-face. Meet privately, close decisively, and keep your schedule your own—no manifests, no terminals, no delays that cost you the transaction.",
  },
];

// Concise, friction-primary recommendation bodies. Compliant: the benefit always
// belongs to OWNING an aircraft (owner-operated, on your own terms)—Steck's role
// is acquisition + management, never operating or selling a flight.
const FRICTION_RESULT = {
  schedule_control:
    "When the schedule is fixed, the airlines' next-available departure sets your day. An aircraft of your own puts that timing back in your hands—you leave when it suits you, and you're often home the same night.",
  regional_access:
    "The places you need are rarely next to a major hub, so commercial routing adds a connection and, often, an overnight. Your own aircraft goes direct to the regional field nearest them—turning a day and a half into an afternoon.",
  productive_time:
    "The hours lost to connections and layovers are hours the trip can't spare. With an aircraft of your own, a trip the airlines stretch across several days is often there and back in one.",
};

const PATH_IDX = { family: 0, business: 1 };

/**
 * Two Doors recommendation—deterministic, driven by the NEW inputs (path +
 * industry + friction + tension). Reuses the preserved levers, headline pool,
 * article routing, and positioning guardrail so the recommended-reading logic
 * is identical to the old survey's (friction, and the economics piece at high
 * tension). Same inputs always yield the same copy.
 */
export function computeTwoDoorsResult({ path, industry, primaryFriction, impactScore }) {
  const friction = FRICTION_LEVER[primaryFriction] ? primaryFriction : "schedule_control";
  const lever = FRICTION_LEVER[friction];
  const bucket = rankBucket(impactScore);
  const pathIdx = PATH_IDX[path] ?? 0;
  const indIdx = INDUSTRIES.findIndex((i) => i.id === industry);
  const industryIdx = indIdx >= 0 ? indIdx : 0;

  // Path × industry × friction × tension → a distinct headline, so no two
  // journeys read alike; weighting keeps the pool well spread.
  const hi = (pathIdx * 5 + industryIdx + FRICTION_IDX[friction] + bucket) % HEADLINES.length;

  // High tension (8–10) → the economics/decision piece; otherwise the report
  // that matches their friction. Identical to the old survey's routing.
  const recommendedArticle = bucket === 2 ? HIGH_IMPACT_ARTICLE : FRICTION_ARTICLE[friction];

  return {
    resultProfile: `${lever.key}-led`,
    emphasisKey: lever.key,
    headline: HEADLINES[hi],
    body: FRICTION_RESULT[friction],
    positioning:
      "Steck's role is acquisition and management—the aircraft is owned and operated on your own terms.",
    recommendedArticle,
  };
}

/**
 * Completion payload for the Two Doors flow—the object a future backend
 * submission would send; today it is only held in local React state.
 */
export function buildTwoDoorsPayload({ path, industry, primaryFriction, impactScore, resultProfile }) {
  return {
    path,
    industry: industry || null,
    primaryFriction,
    impactScore,
    resultProfile,
    completedAt: new Date().toISOString(),
    source: "two_doors_experience",
  };
}

/**
 * One clean completion payload. This is the object a future backend submission
 * would send; today it is only held in local React state.
 */
export function buildAssessmentPayload({ workProfile, primaryFriction, impactScore, resultProfile }) {
  return {
    workProfile,
    primaryFriction,
    impactScore,
    resultProfile,
    completedAt: new Date().toISOString(),
    source: ASSESSMENT_SOURCE,
  };
}
